import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, PieChart, Pie, Cell, Tooltip, XAxis, YAxis,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faPersonWalking, faPhone, faGlobe, faHouse, faCamera, faClipboardList } from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#06b6d4","#8b5cf6","#ec4899"];

const CHANNEL_ICONS: Record<string, any> = {
  "WhatsApp": faComment,
  "Direct / Walk-in": faPersonWalking,
  "Phone": faPhone,
  "Booking.com": faGlobe,
  "Airbnb": faHouse,
  "Instagram": faCamera,
  "Other": faClipboardList,
};

const PERIOD_OPTIONS = [
  { value: "7d",  label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

const SELECT_STYLE: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid #e2e8f0",
  borderRadius: 10, padding: "9px 14px",
  color: "#F2EBD9", fontSize: 13, outline: "none",
  cursor: "pointer",
};

const CHART_TOOLTIP = {
  contentStyle: { background: "#0E1E38", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 12, color: "#F2EBD9", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" },
  labelStyle: { color: "rgba(242,235,217,0.4)" },
};

const CARD: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

interface Channel {
  source: string;
  label: string;
  bookings: number;
  revenue: number;
  cancelled: number;
  cancellationRate: number;
  avgNights: number;
  ADR: number;
  checkedIn: number;
}

interface AnalyticsData {
  channels: Channel[];
  totalBookings: number;
  totalRevenue: number;
  topChannel: string;
  period: string;
}

export default function ChannelAnalyticsPage() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [sortBy, setSortBy] = useState<"revenue" | "bookings" | "ADR" | "cancellationRate">("revenue");

  const authHeaders = {
    Authorization: `Bearer ${localStorage.getItem("ai2_token")}`,
    "x-hotel": localStorage.getItem("ai2_hotel") || "",
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/dashboard/channel-analytics`, {
        params: { period },
        headers: authHeaders,
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [period]);

  const sorted = data?.channels
    ? [...data.channels].sort((a, b) => (b[sortBy] as number) - (a[sortBy] as number))
    : [];

  const pieData = sorted.map((c, i) => ({
    name: c.label,
    value: c.bookings,
    color: COLORS[i % COLORS.length],
  }));

  const revenueBar = sorted.map((c, i) => ({
    name: c.label.replace(" / Walk-in", ""),
    revenue: c.revenue,
    bookings: c.bookings,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <DashboardLayout>
      <div style={{ padding: "28px 32px", minHeight: "100vh" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: "#FDFAF4", margin: 0, letterSpacing: "-0.5px" }}>Channel Analytics</h1>
            <p style={{ fontSize: 13, color: "rgba(242,235,217,0.45)", margin: "4px 0 0" }}>
              Revenue and performance by booking source
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <select value={period} onChange={e => setPeriod(e.target.value)} style={{ ...SELECT_STYLE, minWidth: 140 }}>
              {PERIOD_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} style={{ ...SELECT_STYLE, minWidth: 140 }}>
              <option value="revenue">Sort: Revenue</option>
              <option value="bookings">Sort: Bookings</option>
              <option value="ADR">Sort: ADR</option>
              <option value="cancellationRate">Sort: Cancellation %</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", color: "rgba(242,235,217,0.45)", padding: 80 }}>Loading…</div>
        ) : !data ? (
          <div style={{ textAlign: "center", color: "#f87171", padding: 80 }}>Failed to load data</div>
        ) : (
          <>
            {/* Summary KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
              {[
                { label: "Total Bookings", value: data.totalBookings, sub: "across all channels" },
                { label: "Total Revenue", value: `EGP ${data.totalRevenue.toLocaleString()}`, sub: "non-cancelled" },
                { label: "Top Channel", value: data.channels.find(c => c.source === data.topChannel)?.label || data.topChannel, sub: "by revenue" },
                { label: "Channels Active", value: data.channels.length, sub: "distinct sources" },
              ].map(k => (
                <div key={k.label} style={{ ...CARD, padding: "18px 20px" }}>
                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>{k.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#FDFAF4", margin: "6px 0 2px", letterSpacing: "-0.5px" }}>{k.value}</div>
                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.3)" }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
              {/* Revenue bar */}
              <div style={{ ...CARD, padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4", marginBottom: 16 }}>Revenue by Channel</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={revenueBar} margin={{ left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      {...CHART_TOOLTIP}
                      formatter={(v: number, name: string) => [
                        name === "revenue" ? `EGP ${v.toLocaleString()}` : v,
                        name === "revenue" ? "Revenue" : "Bookings"
                      ]}
                    />
                    <Bar dataKey="revenue" radius={[6,6,0,0]}>
                      {revenueBar.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Bookings pie */}
              <div style={{ ...CARD, padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4", marginBottom: 16 }}>Booking Share</div>
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [`${v} bookings`, "Bookings"]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1 }}>
                    {pieData.map((entry, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", flex: 1 }}>{entry.name.replace(" / Walk-in", "")}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#FDFAF4" }}>
                          {data.totalBookings ? Math.round(entry.value / data.totalBookings * 100) : 0}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed table */}
            <div style={{ ...CARD, overflow: "hidden" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#FDFAF4" }}>Channel Breakdown</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                      {["Channel","Bookings","Revenue","Avg Nights","ADR","Cancellations","Cancel Rate","Currently In"].map(h => (
                        <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "rgba(242,235,217,0.45)", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap", borderBottom: "1px solid #e2e8f0" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((ch, i) => (
                      <tr key={ch.source} style={{ borderTop: "1px solid #f1f5f9" }}
                        onMouseEnter={e => (e.currentTarget.style.background = "rgba(201,168,76,0.04)")}
                        onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                            <FontAwesomeIcon icon={CHANNEL_ICONS[ch.label] || faClipboardList} style={{ fontSize: 14, color: "rgba(242,235,217,0.3)" }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#FDFAF4" }}>{ch.label}</span>
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#F2EBD9", fontWeight: 700 }}>{ch.bookings}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#059669", fontWeight: 700 }}>EGP {ch.revenue.toLocaleString()}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "rgba(242,235,217,0.4)" }}>{ch.avgNights.toFixed(1)}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#FDFAF4", fontWeight: 600 }}>EGP {fmt(ch.ADR)}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "#f87171" }}>{ch.cancelled}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{
                            fontSize: 12, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                            background: ch.cancellationRate > 20 ? "rgba(239,68,68,0.10)" : ch.cancellationRate > 10 ? "rgba(245,158,11,0.10)" : "rgba(16,185,129,0.10)",
                            color: ch.cancellationRate > 20 ? "#ef4444" : ch.cancellationRate > 10 ? "#d97706" : "#059669",
                          }}>
                            {ch.cancellationRate}%
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: "rgba(242,235,217,0.45)", fontWeight: 600 }}>{ch.checkedIn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
